import * as React from 'react';
import Button from 'antd/lib/button';
import {
    WorkflowRunPhasedReleaseWidgetsContainer,
    WorkflowRunPhasedReleaseWidgetsContainerProps,
} from './WorkflowRunPhasedReleaseWidget';
import Flex from 'antd/lib/flex';
import { Link } from 'react-router-dom';
import RightArrowCircledIcon from '@/components/icons/RightArrowCircledIcon';
import { useDispatch } from 'react-redux';
import { trackAppRouteEvent } from '@/actions/trackEvent';
import * as Analytics from '../../../utils/analytics';
import { ReleaseStepType } from '@/types';
import { ArchetypeGroupSummaryDetails } from '@/types/workflowOverview';
import { createStepRunRoute } from '@/helpers/routeHelpers';

export interface WorkflowRunRolloutWidgetsContainerProps extends WorkflowRunPhasedReleaseWidgetsContainerProps {
    orgIdParam: string;
    appIdParam: string;
    releaseIdParam: string;
    workflowEnabledSteps: Partial<Record<ReleaseStepType, boolean>>;
    archetypeGroupDetailsMap: Record<string, ArchetypeGroupSummaryDetails>;
}

export const WorkflowRunRolloutWidgetsContainer: React.FC<WorkflowRunRolloutWidgetsContainerProps> = ({
    orgIdParam,
    appIdParam,
    releaseIdParam,
    phasedGroups,
    appStoreBuildsByDestStepConfigId,
    stabIntegrationId,
    allDestinations,
    selectedDestinationStepConfigIds,
    userSelectedMultipleAPKBuildId,
    workflowEnabledSteps,
    archetypeGroupDetailsMap,
}) => {
    const dispatch = useDispatch();

    const trackViewRolloutMonitoring = () => {
        dispatch(
            trackAppRouteEvent({
                orgId: orgIdParam,
                appId: appIdParam,
                eventName: Analytics.Event.ViewRolloutMonitoring,
            }),
        );
    };

    const haveAnyPhasedAPIs = !!phasedGroups && phasedGroups?.length > 0;

    // TODO-WF-OVERVIEW / TODO-WF-ROLLOUT: add a flag haveAnyMonitoringStats that captures whether we have any APIs for
    // monitoring statistics; then evaluate haveAnyRolloutsData as haveAnyPhasedAPIs || haveAnyMonitoringStats. As below
    // with haveAnyPhasedAPIs / phased widgets, render monitoring widgets conditionally on the value of haveAnyMonitoringStats.
    const haveAnyRolloutsData = haveAnyPhasedAPIs;

    if (!haveAnyRolloutsData) {
        return null;
    }

    const isRolloutsStepMigratedAndEnabled = !!workflowEnabledSteps[ReleaseStepType.Rollout];

    // evaluates to the route of the 0th rollout stepRun, if the new / migrated rollouts step is enabled; otherwise evaluates to the 0th release stepRun
    // TODO-WF-OVERVIEW / TODO-WF-SUMMARY-VIEW: replace this treatment with a link to the rollout stepRuns summary view, once that is implemented
    const rolloutsRoute = React.useMemo(() => {
        let result = '';

        if (archetypeGroupDetailsMap) {
            for (const [_, archGroup] of Object.entries(archetypeGroupDetailsMap)) {
                if (isRolloutsStepMigratedAndEnabled) {
                    if (archGroup.archetype === ReleaseStepType.Rollout) {
                        if (archGroup.archetypeGroupStepRunIds?.length > 0) {
                            result = createStepRunRoute(
                                orgIdParam,
                                appIdParam,
                                releaseIdParam,
                                archGroup.archetypeGroupStepRunIds[0],
                            );
                        }
                        break;
                    }
                } else {
                    if (archGroup.archetype === ReleaseStepType.Takeoff) {
                        if (archGroup.archetypeGroupStepRunIds?.length > 0) {
                            result = createStepRunRoute(
                                orgIdParam,
                                appIdParam,
                                releaseIdParam,
                                archGroup.archetypeGroupStepRunIds[0],
                            );
                        }
                        break;
                    }
                }
            }
        }

        return result;
    }, [archetypeGroupDetailsMap, workflowEnabledSteps]);

    const gotValidRolloutsRoute = !!rolloutsRoute;

    return (
        <Flex
            vertical
            gap={10}
            align={'stretch'}
            style={{
                marginTop: 20,
                marginBottom: 20,
            }}
        >
            {haveAnyPhasedAPIs && (
                <WorkflowRunPhasedReleaseWidgetsContainer
                    phasedGroups={phasedGroups}
                    appStoreBuildsByDestStepConfigId={appStoreBuildsByDestStepConfigId}
                    allDestinations={allDestinations}
                    selectedDestinationStepConfigIds={selectedDestinationStepConfigIds}
                    stabIntegrationId={stabIntegrationId} // TODO-WF-OVERVIEW: tweak component logic to support multiple stab integrations (not yet supported)
                    userSelectedMultipleAPKBuildId={userSelectedMultipleAPKBuildId}
                />
            )}
            {gotValidRolloutsRoute && (
                <Link to={rolloutsRoute} style={{ alignSelf: 'flex-end' }}>
                    <Button
                        type="link"
                        style={{
                            fontSize: 13,
                            fontWeight: 200,
                            paddingRight: 0,
                        }}
                        onClick={() => trackViewRolloutMonitoring()}
                    >
                        <div
                            style={{
                                display: 'inline-flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 8,
                                height: '100%',
                            }}
                        >
                            Rollout
                            <RightArrowCircledIcon />
                        </div>
                    </Button>
                </Link>
            )}
        </Flex>
    );
};
